import { T, useT } from '@transifex/react';
import {
  Avatar,
  AvatarImage,
  Checkbox,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@vidahealth/vida-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

import { formatBirthdayForDisplay } from '#modules/localization';
import { CheckboxCheckIndicator } from '#modules/ui/CheckboxCheckIndicator';
import { ContactSupport } from '#modules/ui/ContactSupport';
import { CtaButton } from '#modules/ui/CtaButton';
import { XIcon } from '#modules/ui/icons';
import { Screen } from '#modules/ui/Screen';
import { ScreenHeading } from '#modules/ui/ScreenHeading';
import { SelectableRow } from '#modules/ui/SelectableRow';
import { TextArea } from '#modules/ui/TextArea';
import { TextField } from '#modules/ui/TextField';

import { useAssessmentQuestions } from '../../hooks/useAssessmentQuestions';
import { useShowContactSupport } from '../../hooks/useShowContactSupport';
import {
  FREE_TEXT_QUESTION_TYPES,
  type Answers,
  type Conditions,
  type AssessmentQuestion,
  type RepeatingListColumn,
  type RepeatingListRow,
  OTHER_CHOICE_VALUE,
} from '../../types/assessmentQuestion';
import { validateAssessmentDate } from '../../utils/validateAssessmentDate';

// One repeating-list row as tracked in component state. `id` is a stable,
// render-independent key (NOT the row's position — that shifts whenever an
// earlier row is removed, which would otherwise remount every input after it
// and drop focus/IME state). `values` is column name → typed text, always
// pre-seeded with every current column so a controlled TextField never
// receives `undefined`.
type RepeatingListRowState = { id: string; values: RepeatingListRow };

type AssessmentContentProps = {
  questions: AssessmentQuestion[];
  conditions?: Conditions;
  /** Called with the full answer map once the group's last question is answered. */
  onContinue: (answers: Answers) => void;
  isSubmitting?: boolean;
  /**
   * Per-assessment testID prefix (e.g. `phq-gad`). Derives stable selectors for the
   * Continue button, the date input, and each answer row — keyed by the active
   * question so option values that repeat across questions (PHQ/GAD scales) stay
   * unique. Omitted → no testIDs.
   */
  testIDPrefix?: string;
};

// Human-friendly testID segment derived from a choice's display label:
// lowercased, non-alphanumeric runs collapsed to single hyphens, trimmed
// (e.g. "More than half the days" → "more-than-half-the-days", "Female" →
// "female"). Only one question renders at a time, so a label-based id is unique
// in the tree even when answer labels repeat across questions (PHQ/GAD scales).
const slugify = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// RN port of stylos's SurveyPage: shows one question at a time. Unlike Sparta
// (which auto-advances on choice tap), selecting a choice only highlights it —
// the user advances with an explicit Continue button, for consistency with the
// rest of the onboarding flow. 'date' questions use a masked input. When the
// last question is answered, onContinue fires.
export function AssessmentContent({
  questions,
  conditions,
  onContinue,
  isSubmitting = false,
  testIDPrefix,
}: AssessmentContentProps) {
  const t = useT();
  const showContactSupport = useShowContactSupport();
  const [answers, setAnswers] = useState<Answers>({});
  // Holds the pending selection for the active question — one entry for
  // single-select (radiogroup/scale), many for multi-select (checkbox).
  const [selected, setSelected] = useState<string[]>([]);
  const [dateValue, setDateValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [otherInput, setOtherInput] = useState('');
  // Holds the full date error message (with the concrete min date baked in), or
  // null when valid — so the min only lives in one place: the message we set.
  const [dateError, setDateError] = useState<string | null>(null);
  const [showRequiredError, setShowRequiredError] = useState(false);
  // Rows for the active 'repeatingList' question. Rebuilt by the effect below
  // whenever the active question changes — NOT reset inline in `commitAnswer`
  // like the other pending-value state, because the right reset value (how
  // many rows, which columns) depends on the NEXT question, which isn't known
  // yet at commit time (it's resolved by `useAssessmentQuestions` after the
  // question index advances).
  const [rows, setRows] = useState<RepeatingListRowState[]>([]);
  // Mirrors `rows`, written synchronously by every mutator below (`addRow`,
  // `removeRow`, the reset effect) at the same moment as the matching
  // `setRows` call. `rows` itself only reflects the LAST render React
  // committed, so two mutations dispatched before that commit happens — a
  // rapid double-tap or multi-touch batched into one React update — would
  // both read the SAME stale `rows.length` off state: `removeRow`'s
  // `length <= 1` floor could then pass twice and empty the list despite it,
  // and `addRow`'s announced row number could repeat. Reading/writing this
  // ref instead makes every mutation see the true, up-to-the-instant row
  // count regardless of what has or hasn't rendered yet.
  const rowsRef = useRef<RepeatingListRowState[]>([]);
  // Feeds the ARIA live region in the render — the web half of announcing a
  // row add/remove. See `announce` below for why both halves are needed.
  const [statusMessage, setStatusMessage] = useState('');
  // Toggled by `announce` below on every call — see its comment for why.
  const flip = useRef(false);
  const nextRowId = useRef(0);

  const setRowsAndRef = useCallback((next: RepeatingListRowState[]) => {
    rowsRef.current = next;
    setRows(next);
  }, []);

  const { activeQuestion, setNextActiveQuestion } = useAssessmentQuestions(questions, conditions);

  const createRow = useCallback((columns: RepeatingListColumn[]): RepeatingListRowState => {
    const id = `row-${nextRowId.current}`;
    nextRowId.current += 1;
    return {
      id,
      values: Object.fromEntries(columns.map((column) => [column.name, ''])),
    };
  }, []);

  const isMultiSelect = activeQuestion?.isMultiSelect === true;
  const hasOtherItem = isMultiSelect && activeQuestion?.showOtherItem === true;

  // Commit the answer for the active question, reset the pending selection, and
  // advance (or finish the group). A `string[]` value comes from a multi-select
  // (checkbox) question, a `RepeatingListRow[]` from a repeatingList question;
  // conditions never target either, so `Answer`'s string `questionValue` gets
  // '' in both cases (no stop/jump can match). `otherValue` is the trimmed
  // inline Other text; omitted when Other is unchecked or the field is blank
  // so the derived key is not sent as "".
  const commitAnswer = useCallback(
    (value: string | string[] | RepeatingListRow[], otherValue?: string) => {
      if (!activeQuestion) return;
      const nextAnswers: Answers = { ...answers, [activeQuestion.key]: value };
      if (otherValue && activeQuestion.otherKey) {
        nextAnswers[activeQuestion.otherKey] = otherValue;
      }
      setAnswers(nextAnswers);
      setSelected([]);
      setDateValue('');
      setDateError(null);
      setTextValue('');
      setOtherInput('');
      setShowRequiredError(false);
      setNextActiveQuestion(
        { questionKey: activeQuestion.key, questionValue: Array.isArray(value) ? '' : value },
        () => onContinue(nextAnswers)
      );
    },
    [activeQuestion, answers, setNextActiveQuestion, onContinue]
  );

  // Whether `value` names a choice authored with `exclusive: true` on the
  // active question (e.g. health_conditions' "None of these conditions").
  // Only choice questions carry `exclusive`, so this is vacuously false for
  // every other question type.
  const isExclusiveValue = useCallback(
    (value: string) =>
      activeQuestion?.choices.some(
        (choice) => choice.value === value && choice.exclusive === true
      ) ?? false,
    [activeQuestion]
  );

  // Toggle a choice: multi-select adds/removes it from the set; single-select
  // replaces the whole selection with just this value. Within multi-select,
  // an `exclusive` choice (e.g. "None of these conditions") enforces mutual
  // exclusion in both directions: selecting it drops every other selection,
  // and selecting anything else drops a previously-selected exclusive one —
  // same rule as HealthConditionsScreen's toggleCondition/toggleNone, unified
  // into one handler driven by the flag instead of two handlers plus local
  // `noneChecked` state.
  const toggleChoice = useCallback(
    (value: string) => {
      setShowRequiredError(false);
      setSelected((prev) => {
        if (!isMultiSelect) return [value];
        if (isExclusiveValue(value)) {
          return prev.includes(value) ? [] : [value];
        }
        if (prev.includes(value)) return prev.filter((v) => v !== value);
        return [...prev.filter((v) => !isExclusiveValue(v)), value];
      });
    },
    [isMultiSelect, isExclusiveValue]
  );

  const isDate = activeQuestion?.questionType === 'date';
  // 'comment' → multiline input (TextArea); 'text'/'number'/'timezone' →
  // single-line input (TextField). All submit the typed string, unlike
  // choice questions which submit a chosen value/values. `date` is excluded
  // from FREE_TEXT_QUESTION_TYPES: it gets its own masked input above, not a
  // plain TextField/TextArea, even though it's also free-response on the wire.
  const isComment = activeQuestion?.questionType === 'comment';
  const isFreeText = FREE_TEXT_QUESTION_TYPES.has(activeQuestion?.questionType ?? '');
  // SurveyJS models a numeric free-text answer (e.g. goal weight, highest
  // adult weight) as a `text` element with `inputType: "number"`; legacy
  // screeners instead send `questionType: "number"` directly — either way,
  // switch the on-screen keyboard, same as the date field already does.
  const isNumericFreeText =
    activeQuestion?.inputType === 'number' || activeQuestion?.questionType === 'number';
  const isRepeatingList = activeQuestion?.questionType === 'repeatingList';

  // Update one cell of one row. Matched by `row.id`, not position, so it stays
  // correct after an earlier row is removed and the rest shift up.
  const updateRowValue = useCallback(
    (rowId: string, columnName: string, text: string) => {
      setShowRequiredError(false);
      setRowsAndRef(
        rowsRef.current.map((row) =>
          row.id === rowId ? { ...row, values: { ...row.values, [columnName]: text } } : row
        )
      );
    },
    [setRowsAndRef]
  );

  // Adding or removing a row changes the screen without moving focus, so a
  // screen reader is told nothing on its own: the member presses the control
  // and gets silence, with no way to know it worked short of swiping the whole
  // screen. WCAG 2.1 4.1.3 (Status Messages, AA) is exactly this case.
  //
  // It takes BOTH mechanisms, because neither covers every platform we ship on:
  //   - `announceForAccessibility` is the native (iOS/Android) API. In
  //     react-native-web it is an empty function
  //     (`exports/AccessibilityInfo/index.js`) — there is no browser API that
  //     makes a screen reader speak on command, so there is nothing for it to
  //     do. Calling it on web is inert, not harmful.
  //   - The web mechanism is an ARIA live region: a node marked `aria-live`
  //     whose text changes. That's what `statusMessage` below feeds (see the
  //     live-region Text in the render).
  // Both are no-ops when no screen reader is running, so neither needs gating.
  //
  // The live region only announces when its text CONTENT actually changes —
  // two consecutive calls with the byte-identical message (e.g. removing row
  // 2, then removing what's now row 2 again) would hit React's same-value
  // `setState` bailout: no re-render, the DOM text node never changes, and
  // the second announcement is silently swallowed on web (native is
  // unaffected — `announceForAccessibility` is an imperative call, not
  // state, so every call speaks regardless of repetition). `flip` toggles a
  // trailing zero-width space on/off every call, so the string handed to
  // `statusMessage` always differs from the PREVIOUS one even when `message`
  // itself repeats — while `announceForAccessibility` still gets the clean
  // `message`, and a screen reader never reads a zero-width space, so what's
  // actually spoken is identical on both platforms either way.
  const announce = useCallback((message: string) => {
    flip.current = !flip.current;
    setStatusMessage(flip.current ? `${message}\u200B` : message);
    AccessibilityInfo.announceForAccessibility(message);
  }, []);

  // No upper bound — there's no `maxRowCount` on the wire today (see
  // AssessmentQuestion.initialRowCount's doc comment).
  //
  // Builds off `rowsRef.current`, not the `rows` state read at render time —
  // see `rowsRef`'s comment. The announced number comes off the resulting
  // array's length rather than `rowsRef.current.length + 1` for the same
  // reason: it's the number this call actually produced, not a value computed
  // from a count that could already be stale by the time this runs.
  const addRow = useCallback(() => {
    const next = [...rowsRef.current, createRow(activeQuestion?.columns ?? [])];
    setRowsAndRef(next);
    announce(t('Item {number} added', { number: next.length }));
  }, [activeQuestion, createRow, setRowsAndRef, announce, t]);

  // Floors at 1 row: dropping to zero would leave a required question with no
  // field to type into at all — Continue's required error would have nothing
  // for the member to act on (the same class of dead-end T1 fixed for a blank
  // free-text question). A single blank row can still be left empty and
  // skipped via the optional-skip branch below, so "no items" is still
  // expressible without needing an empty row LIST. The render below hides the
  // remove control once this floor is hit, so this guard is a backstop, not
  // the only thing preventing a dead no-op control from being shown.
  //
  // Takes only `rowId` — the removed row's number for the announcement is
  // derived from `rowsRef.current` below, not passed in from the render's map
  // index. That index is a snapshot from whatever render created this
  // particular closure; `rowsRef.current` is instead read at the moment this
  // call actually runs, so a second `removeRow` batched right behind a first
  // one (two remove controls pressed together, or one pressed twice before a
  // re-render) sees the row the first call already removed, not a copy of the
  // pre-removal list. That's what keeps the floor above from passing for both
  // calls and what keeps the announced numbers from repeating.
  const removeRow = useCallback(
    (rowId: string) => {
      const prev = rowsRef.current;
      if (prev.length <= 1) return;
      const removedIndex = prev.findIndex((row) => row.id === rowId);
      if (removedIndex === -1) return;
      setRowsAndRef(prev.filter((row) => row.id !== rowId));
      // Same silence as addRow, and worse: the removed row takes the button
      // that had focus with it, so the member is left with no anchor at all
      // unless the change is spoken.
      announce(t('Item {number} removed', { number: removedIndex + 1 }));
    },
    [setRowsAndRef, announce, t]
  );

  // Validate the date and reflect the result into `dateError`: a message on
  // failure, or null to clear. Distinct failures get distinct copy — a `null`
  // normalizedDate means the input couldn't be parsed (blank / incomplete /
  // not a real date), so a bounds message would be misleading. A parsed date
  // past an authored `max` earns the ceiling copy; any other parsed-but-invalid
  // date earns the floor copy. Bounds come straight off the validation result,
  // so they live only in the message.
  const validateDate = useCallback(() => {
    const { isValid, min, max, reason, normalizedDate } = validateAssessmentDate(dateValue, {
      min: activeQuestion?.minDate,
      max: activeQuestion?.maxDate,
    });
    if (!isValid) {
      setDateError(
        reason === 'unparseable'
          ? t('Enter a valid date (MM/DD/YYYY).')
          : reason === 'after-max' && max
            ? t('Enter a date on or before {date}.', { date: formatBirthdayForDisplay(max) })
            : t('Enter a date on or after {date}.', { date: formatBirthdayForDisplay(min) })
      );
      return null;
    }
    setDateError(null);
    return normalizedDate;
  }, [dateValue, t, activeQuestion?.minDate, activeQuestion?.maxDate]);

  // Surface the error as soon as the field loses focus (not just on Continue).
  // Blur only fires after the field's been focused, so an empty value here means
  // the user left it blank — flag that too (validateAssessmentDate('') is invalid).
  const onDateBlur = useCallback(() => {
    validateDate();
  }, [validateDate]);

  // Shared onChangeText for the free-text branch — same handler regardless of
  // which component (TextField or TextArea) is rendered.
  const handleTextChange = useCallback((text: string) => {
    setTextValue(text);
    setShowRequiredError(false);
  }, []);

  // Explicitly `false` → skippable. Absent defaults to required, so the legacy
  // screeners (which never set the flag) keep their behavior.
  const isOptional = activeQuestion?.isRequired === false;

  const onContinuePress = useCallback(() => {
    if (!activeQuestion) return;
    if (isDate) {
      // An optional date left blank is a skip; anything typed still validates.
      if (isOptional && dateValue.trim() === '') {
        commitAnswer('');
        return;
      }
      const normalizedDate = validateDate();
      if (normalizedDate) commitAnswer(normalizedDate);
      return;
    }
    if (isFreeText) {
      const trimmed = textValue.trim();
      if (trimmed === '') {
        // Same skip-vs-block split as choice questions: an optional blank
        // answer commits '' silently, a required one surfaces the error.
        if (isOptional) {
          commitAnswer('');
          return;
        }
        setShowRequiredError(true);
        return;
      }
      commitAnswer(trimmed);
      return;
    }
    if (isRepeatingList) {
      // A row "counts" if at least one of its cells has non-whitespace
      // content; a fully-blank row (e.g. a trailing row the member added and
      // then didn't fill in) is dropped rather than submitted as an all-empty
      // object. Kept rows are trimmed but NOT otherwise validated — v1 has no
      // per-cell required check (e.g. dosage filled but name blank still
      // submits), matching the ticket's "open-ended items" framing rather
      // than inventing per-column requiredness the schema doesn't carry.
      const filledRows = rows.reduce<RepeatingListRow[]>((acc, row) => {
        const trimmedValues: RepeatingListRow = {};
        let hasValue = false;
        for (const [columnName, value] of Object.entries(row.values)) {
          const trimmed = value.trim();
          trimmedValues[columnName] = trimmed;
          if (trimmed !== '') hasValue = true;
        }
        if (hasValue) acc.push(trimmedValues);
        return acc;
      }, []);
      if (filledRows.length === 0) {
        // Same skip-vs-block split as every other question type: optional +
        // nothing filled in commits an empty list silently, required blocks.
        // A repeatingList with no valid columns joins the optional side of
        // that split even when required: every row's `values` is `{}` (no
        // column names to key into), so `filledRows` can NEVER be non-empty —
        // blocking would leave a required question showing a "please answer"
        // error with no field on screen to act on it, permanently. `columns`
        // reaching here empty means malformed authoring (`columns: []`, or —
        // before toDynamicAssessmentQuestions' shorthand support — every
        // column unnamed), not a member choice, so it shouldn't be able to
        // trap them.
        if (isOptional || (activeQuestion.columns ?? []).length === 0) {
          commitAnswer([]);
          return;
        }
        setShowRequiredError(true);
        return;
      }
      commitAnswer(filledRows);
      return;
    }
    if (selected.length === 0) {
      // Skipping an optional question still commits the key (an empty array for
      // multi-select, '' for single) rather than omitting it — the
      // dynamic-assessment server registers the element name as an
      // allowed_signal_key and rejects an empty answers map (same reason the
      // gate auto-advance sends `{ _gate: '' }`).
      if (isOptional) {
        commitAnswer(isMultiSelect ? [] : '');
        return;
      }
      setShowRequiredError(true);
      return;
    }
    // Multi-select submits the full set as an array; single-select submits the
    // one chosen value as a string. Inline Other text rides a second key.
    if (isMultiSelect) {
      if (hasOtherItem && selected.includes(OTHER_CHOICE_VALUE)) {
        const trimmedOther = otherInput.trim();
        if (activeQuestion.isOtherRequired === true && trimmedOther === '') {
          setShowRequiredError(true);
          return;
        }
        commitAnswer(selected, trimmedOther || undefined);
        return;
      }
      commitAnswer(selected);
      return;
    }
    commitAnswer(selected[0] ?? '');
  }, [
    activeQuestion,
    isDate,
    isFreeText,
    isRepeatingList,
    isOptional,
    isMultiSelect,
    dateValue,
    validateDate,
    textValue,
    rows,
    selected,
    otherInput,
    hasOtherItem,
    commitAnswer,
  ]);

  // Rebuild `rows` for the active 'repeatingList' question. Keyed on
  // `activeQuestion?.key` (the question's own identity), NOT on the
  // `activeQuestion` object itself: `questions` is rebuilt with fresh array +
  // object identities on every parent re-render (DynamicAssessmentFlow calls
  // toDynamicAssessmentQuestions inline, unmemoized), so `activeQuestion` is a
  // new reference on every such re-render even when it's the SAME question.
  // Depending on the object would re-run this effect on every incidental
  // parent re-render (e.g. a rules-engine context update while the member is
  // mid-question) and silently wipe whatever they'd already typed — `key`
  // only changes when the member actually advances to a different question.
  useEffect(() => {
    if (activeQuestion?.questionType !== 'repeatingList') return;
    const startingRowCount = activeQuestion.initialRowCount ?? 1;
    setRowsAndRef(
      Array.from({ length: startingRowCount }, () => createRow(activeQuestion.columns ?? []))
    );
    // activeQuestion/columns/initialRowCount/questionType are read from the
    // SAME render whose `key` triggered this run (effects close over the
    // render they were defined in), so they're always in sync with `key`
    // despite not being listed — see the comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestion?.key, createRow, setRowsAndRef]);

  useEffect(() => {
    if (!selected.includes(OTHER_CHOICE_VALUE)) setOtherInput('');
  }, [selected]);

  if (!activeQuestion) return null;

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <VStack space='md'>
          {/* Pinned above Continue rather than trailing the last row: the list
              grows without bound, so an inline button walks off the bottom of
              the screen exactly as it becomes most useful. Sharing the footer
              with Continue also reads the two as one control pair on a screen
              whose whole job is "add rows, then continue". */}
          {isRepeatingList ? (
            <CtaButton
              type='secondary'
              isDisabled={isSubmitting}
              onPress={addRow}
              testID={testIDPrefix ? `${testIDPrefix}-add-row` : undefined}
            >
              <T _str='+ Add another' />
            </CtaButton>
          ) : null}
          <CtaButton
            isBusy={isSubmitting}
            isDisabled={isSubmitting}
            onPress={onContinuePress}
            testID={testIDPrefix ? `${testIDPrefix}-continue` : undefined}
          >
            <T _str='Continue' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={activeQuestion.text ?? ''}
          subtitle={activeQuestion.additionalInfo ?? undefined}
        />

        {showRequiredError ? (
          <Text className='text-error-700'>
            {isFreeText ||
            isRepeatingList ||
            (hasOtherItem && selected.includes(OTHER_CHOICE_VALUE)) ? (
              <T _str='Please enter an answer to continue.' />
            ) : (
              <T _str='Please select an answer to continue.' />
            )}
          </Text>
        ) : null}

        {isDate ? (
          <TextField
            value={dateValue}
            onChangeText={(text) => {
              setDateValue(text);
              setDateError(null);
            }}
            onBlur={onDateBlur}
            format={formatBirthdayForDisplay}
            placeholder={t('MM / DD / YYYY')}
            inputMode='numeric'
            keyboardType='number-pad'
            errorMessage={dateError ?? undefined}
            testID={testIDPrefix ? `${testIDPrefix}-input` : undefined}
          />
        ) : isFreeText ? (
          isComment ? (
            <TextArea
              value={textValue}
              onChangeText={handleTextChange}
              placeholder={t('Type your answer')}
              testID={testIDPrefix ? `${testIDPrefix}-input` : undefined}
            />
          ) : (
            <TextField
              value={textValue}
              onChangeText={handleTextChange}
              placeholder={t('Type your answer')}
              inputMode={isNumericFreeText ? 'numeric' : undefined}
              keyboardType={isNumericFreeText ? 'number-pad' : undefined}
              testID={testIDPrefix ? `${testIDPrefix}-input` : undefined}
            />
          )
        ) : isRepeatingList ? (
          <VStack space='lg'>
            {/* The web half of announcing a row add/remove (see `announce`):
                an ARIA live region, whose text a screen reader reads when it
                changes. Web only — on native `announceForAccessibility` does
                the job, and an extra node there would just be one more stop
                for a swipe to land on. Positioned off-screen rather than
                hidden: `display: none` and `aria-hidden` content is not
                announced at all. */}
            {Platform.OS === 'web' ? (
              <Text
                aria-live='polite'
                className='absolute -left-[9999px]'
                testID={testIDPrefix ? `${testIDPrefix}-status` : undefined}
              >
                {statusMessage}
              </Text>
            ) : null}
            {rows.map((row, index) => (
              <VStack
                key={row.id}
                space='md'
                className='rounded-vui-lg border border-outline-200 p-4'
              >
                {/* No per-row heading: the only label we could show is a generic
                    "Item {n}" — `matrixdynamic` carries no authorable row title
                    (the builder's MatrixForm exposes only `columns` + `rowCount`),
                    so the same hardcoded word would head every list, medicines or
                    medical history alike. The card border separates rows and the
                    first column's label carries the row number instead. */}
                {rows.length > 1 ? (
                  <HStack className='justify-end'>
                    <Pressable
                      onPress={() => removeRow(row.id)}
                      accessibilityRole='button'
                      accessibilityLabel={t('Remove item {number}', { number: index + 1 })}
                      // The glyph is 18px, under the 24×24 minimum tap target
                      // WCAG 2.2 2.5.8 (AA) asks for, so the control is padded
                      // out to 30px. Padding rather than `hitSlop`: hitSlop
                      // only exists on native (react-native-web's Pressable
                      // has no such prop), and this has to hold on web too.
                      // Padding is a real box, so it works on every platform —
                      // at the cost of ~12px of row height, which is why the
                      // icon itself stays 18px.
                      className='p-1.5'
                      testID={testIDPrefix ? `${testIDPrefix}-remove-row-${index}` : undefined}
                    >
                      <XIcon className='text-typography-500' width={18} height={18} />
                    </Pressable>
                  </HStack>
                ) : null}
                {(activeQuestion.columns ?? []).map((column, columnIndex) => {
                  // Once a second row exists the bare column title repeats down
                  // the screen ("Medication", "Medication"), so the row number
                  // rides on the authored title. Both halves are data (the
                  // author's column title and an index), so there is nothing to
                  // translate here. Only the first column carries it, which is
                  // enough to mark where a row begins without stamping the
                  // number on every field in the card.
                  const numbered = rows.length > 1 && columnIndex === 0;
                  const fieldLabel = numbered ? `${column.title} ${index + 1}` : column.title;
                  return (
                    <TextField
                      key={column.name}
                      value={row.values[column.name] ?? ''}
                      onChangeText={(text) => updateRowValue(row.id, column.name, text)}
                      // The floating label, same as every other input in the
                      // flow (date, account creation, member contact) — it stays
                      // visible once filled rather than being replaced, and
                      // TextField derives the accessible name from it, so no
                      // separate accessibilityLabel/aria-label is needed here.
                      // It also keeps a card of repeating rows compact, which
                      // matters more here than anywhere else: the labels repeat
                      // once per column per row.
                      label={fieldLabel}
                      testID={
                        testIDPrefix ? `${testIDPrefix}-input-${index}-${column.name}` : undefined
                      }
                    />
                  );
                })}
              </VStack>
            ))}
          </VStack>
        ) : (
          <VStack space='lg'>
            {activeQuestion.choices.map((choice) => {
              const isSelected = selected.includes(choice.value);
              const showOtherField =
                hasOtherItem && choice.value === OTHER_CHOICE_VALUE && isSelected;
              return (
                <VStack key={choice.value} space='sm'>
                  <SelectableRow
                    isSelected={isSelected}
                    selectionRole={isMultiSelect ? 'checkbox' : 'radio'}
                    onPress={() => toggleChoice(choice.value)}
                    accessibilityLabel={choice.text}
                    // Checkbox rows (label-only) get the taller HealthConditions
                    // padding; avatar rows already grow to fit the `lg` icon, so
                    // extra padding there would make them too tall.
                    className={isMultiSelect ? 'py-6' : undefined}
                    testID={testIDPrefix ? `${testIDPrefix}-${slugify(choice.text)}` : undefined}
                  >
                    {isMultiSelect ? (
                      <Checkbox value={choice.value} isChecked={isSelected} pointerEvents='none'>
                        <CheckboxCheckIndicator />
                      </Checkbox>
                    ) : null}
                    {choice.imageLink ? (
                      <Avatar size='lg' className='bg-[#FDF6EE]'>
                        <AvatarImage source={{ uri: choice.imageLink }} alt={choice.text} />
                      </Avatar>
                    ) : null}
                    <Text size='md' weight='semibold' className='flex-1 text-typography-900'>
                      {choice.text}
                    </Text>
                  </SelectableRow>
                  {showOtherField ? (
                    <TextField
                      value={otherInput}
                      onChangeText={(text) => {
                        setOtherInput(text);
                        setShowRequiredError(false);
                      }}
                      placeholder={activeQuestion.otherPlaceholder ?? t('Please specify')}
                      testID={testIDPrefix ? `${testIDPrefix}-other-text` : undefined}
                    />
                  ) : null}
                </VStack>
              );
            })}
          </VStack>
        )}

        {showContactSupport ? <ContactSupport showPhone /> : null}
      </VStack>
    </Screen>
  );
}
